/**
 * Flow utility functions
 */

import type { FunnelDefinitionNode, FunnelDefinitionRootNode } from '../types/nodes'
import type { Flow, Question, Offer } from '../types/data'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { stripHtml, renderText } from '@shared/services'
import { uuid } from '@dashboard/services/uuid'
import { emailSchema } from '@dashboard/formValidationschemas'

export const MAX_EMAIL_RECIPIENTS = 20

/**
 * Check if a funnel is empty (has no child nodes)
 */
export const isEmptyFunnel = (funnelDefinition: FunnelDefinitionRootNode): boolean =>
  funnelDefinition.child.length === 0

/**
 * Extract score names from adjustScore() and setScore() calls in code
 * Uses static analysis to find adjustScore('scoreName', ...) and setScore('scoreName', ...) patterns
 */
export const getScoresFromCode = (code: string): string[] => {
  if (!code || code.trim() === '') {
    return []
  }

  const scores: string[] = []

  // Remove comments to avoid false positives
  const codeWithoutComments = code
    .replace(/\/\/.*$/gm, '') // Remove single-line comments
    .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments

  // Match adjustScore('scoreName', ...) or adjustScore("scoreName", ...)
  // and setScore('scoreName', ...) or setScore("scoreName", ...)
  // Variable references won't be caught (that's acceptable)
  const regex = /(?:adjustScore|setScore)\s*\(\s*['"`]([^'"`]+)['"`]/g
  let match

  while ((match = regex.exec(codeWithoutComments)) !== null) {
    if (match[1]) {
      scores.push(match[1])
    }
  }

  return [...new Set(scores)]
}

/**
 * Check if code returns a displayable value
 * Uses static analysis to detect return statements (no execution)
 */
export const doesCodeReturnValue = (code: string): boolean => {
  if (!code || code.trim() === '') {
    return false
  }

  // Remove comments to avoid false positives
  const codeWithoutComments = code
    .replace(/\/\/.*$/gm, '') // Remove single-line comments
    .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multi-line comments

  // Count brace depth to find top-level returns
  let braceDepth = 0
  let inFunction = false
  const lines = codeWithoutComments.split('\n')

  for (const line of lines) {
    const trimmedLine = line.trim()

    // Track if we're entering a function
    if (/\bfunction\s*\(/.test(trimmedLine) || /[=]>\s*\{/.test(trimmedLine)) {
      inFunction = true
    }

    // Count braces
    for (const char of line) {
      if (char === '{') {
        braceDepth++
      }
      if (char === '}') {
        braceDepth--
        if (braceDepth === 0) {
          inFunction = false
        }
      }
    }

    // Check for return at top level (not inside nested functions)
    if (!inFunction && /\breturn\b/.test(trimmedLine)) {
      // Check what comes after return
      const returnMatch = trimmedLine.match(/\breturn\s+(.+)/)
      if (returnMatch) {
        const matchedValue = returnMatch[1]
        if (matchedValue === undefined) {
          throw new Error('Return expression match is missing its value')
        }
        const returnValue = matchedValue.trim()
        // Make sure it's not just "return;" or "return undefined"
        if (returnValue && returnValue !== ';' && returnValue !== 'undefined' && returnValue !== 'undefined;') {
          return true
        }
      }
    }
  }

  return false
}

/**
 * Check if a node terminates the flow
 */
export const doesNodeTerminate = (node: FunnelDefinitionNode): boolean => {
  if (node.type === 'sg' && node.split) {
    if (node.segmentGroupId && node.split.length) {
      return node.split.every((split) => split.child.some((child) => doesNodeTerminate(child)))
    }
    return false
  }

  if (node.type === 'yesno') {
    return node.yes.some((child) => doesNodeTerminate(child)) && node.no.some((child) => doesNodeTerminate(child))
  }

  if (node.type === 'random') {
    return (node.split as Exclude<typeof node.split, false>).every((split) =>
      split.child.some((child) => doesNodeTerminate(child))
    )
  }

  if (node.type === 'highscore' && node.split) {
    return node.split.every((split) => split.child.some((child) => doesNodeTerminate(child)))
  }

  // Code nodes terminate the flow if they return a value
  if (node.type === 'code') {
    return doesCodeReturnValue(node.code)
  }

  return node.type === 'exit' || node.type === 'offer' || node.type === 'redirect' || node.type === 'message'
}

/**
 * Check if a node is properly configured
 * @param node - The node to check
 * @param context - Additional context for validation (offers, questions, etc.)
 */
export const isNodeSetUp = (
  node: FunnelDefinitionNode,
  context?: {
    offers?: Offer[]
    questions?: Question[]
    segmentGroups?: any[]
  }
): boolean => {
  if (node.type === 'sg') {
    // Check if segment group exists
    if (node.segmentGroupId === null) {
      return false
    }
    if (node.segmentGroupId?.startsWith('raq')) {
      return true
    }
    if (context?.segmentGroups) {
      return context.segmentGroups.some((sg) => sg.id === node.segmentGroupId)
    }
    return true
  }

  if (node.type === 'yesno') {
    const s = JSON.stringify(node.condition)
    return s !== JSON.stringify({ or: [] }) && s !== '{}'
  }

  if (node.type === 'offer') {
    if (node.offerId === null) {
      return false
    }

    // Check if offer exists
    if (context?.offers) {
      const offer = context.offers.find((o) => o.id === node.offerId)
      if (!offer) {
        return false
      }

      // Check if form can be submitted (has fields)
      if (offer.triggeredAction?.type !== 'link') {
        const fields = offer.triggeredAction?.settings?.fields || []
        return fields.length > 0
      }
    }
    return true
  }

  if (node.type === 'redirect') {
    return node.url !== ''
  }

  if (node.type === 'code') {
    return node.code !== ''
  }

  if (node.type === 'aigenerate') {
    return false
  }

  if (node.type === 'webhook') {
    return node.url !== '' && node.url.startsWith('https://')
  }

  if (node.type === 'email') {
    const recipients = node.to
      .split(',')
      .map((address) => address.trim())
      .filter(Boolean)
    return (
      recipients.length > 0 &&
      recipients.length <= MAX_EMAIL_RECIPIENTS &&
      recipients.every((address) => emailSchema.safeParse(address).success) &&
      stripHtml(node.subject).trim() !== '' &&
      stripHtml(node.body).trim() !== ''
    )
  }

  if (node.type === 'embed') {
    return node.content !== null && (node.target.kind === 'page' || node.target.selector.trim() !== '')
  }

  if (node.type === 'data') {
    // Data node must have at least one valid action
    if (!node.actions || node.actions.length === 0) {
      return false
    }

    // Check each action is properly configured
    return node.actions.every((action: any) => {
      if (action.type === 'score') {
        return action.score && action.adjust !== undefined
      }
      if (action.type === 'field') {
        return action.field && action.value
      }
      if (action.type === 'variable') {
        return action.variableName && action.variableValue
      }
      if (action.type === 'add_tag') {
        return action.tagId
      }
      if (action.type === 'remove_tag') {
        return action.tagId
      }
      return false
    })
  }

  return true
}

/**
 * Check if flow has questions
 */
export const flowHasQuestions = (funnelDefinition: FunnelDefinitionRootNode): boolean => {
  let hasQuestions = false
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask) {
      hasQuestions = true
    }
  })
  return hasQuestions
}

/**
 * Check if flow has Display nodes (UI-requiring nodes)
 * Display nodes are: questions (sg with ask=true), offers, messages, and code nodes that return values
 * These nodes require a widget/UI to be shown and cannot run in system flows
 */
export const flowHasDisplayNodes = (funnelDefinition: FunnelDefinitionRootNode): boolean => {
  let hasDisplayNodes = false
  funnelDefinitionEach(funnelDefinition, (node) => {
    // Questions are Display nodes
    if (node.type === 'sg' && node.ask) {
      hasDisplayNodes = true
    }
    // Offers are Display nodes
    if (node.type === 'offer') {
      hasDisplayNodes = true
    }
    // Messages are Display nodes
    if (node.type === 'message') {
      hasDisplayNodes = true
    }
    // Code nodes that return values are Display nodes
    if (node.type === 'code' && doesCodeReturnValue(node.code)) {
      hasDisplayNodes = true
    }
  })
  return hasDisplayNodes
}

/**
 * Extract scores used in flow
 */
export const scoresInFlow = (funnelDefinition: FunnelDefinitionRootNode, questions: Question[] = []): string[] => {
  let scores: string[] = []

  funnelDefinitionEach(funnelDefinition, (node) => {
    // Get scores from data nodes
    if (node.type === 'data') {
      node.actions.forEach((action: { type: string; score?: string }) => {
        if (action.type === 'score' && action.score) {
          scores.push(action.score)
        }
      })
    }

    // Get scores from question answer options
    if (node.type === 'sg' && node.ask && node.segmentGroupId) {
      const question = questions.find(
        (q) =>
          // Support both segmentGroupId and dimensionId for compatibility
          q.segmentGroupId === node.segmentGroupId || (q as any).dimensionId === node.segmentGroupId
      )
      if (question && question.options) {
        question.options.forEach((option) => {
          const opt = option as any
          if (opt.scores && Array.isArray(opt.scores)) {
            opt.scores.forEach((s: any) => {
              if (s.score && s.amount !== undefined && s.amount !== 0) {
                scores.push(s.score)
              }
            })
          }
        })
      }
    }

    // Text questions that score their answers
    if (node.type === 'sg' && node.ask && node.segmentGroupId?.startsWith('raq_')) {
      const name = questions.find((q) => q.id === node.segmentGroupId)?.fill?.score?.name
      if (name) {
        scores.push(name)
      }
    }

    // Get scores from code nodes using adjustScore() calls
    if (node.type === 'code' && node.code) {
      const codeScores = getScoresFromCode(node.code)
      scores.push(...codeScores)
    }
  })

  return [...new Set(scores)]
}

/**
 * Extract variable names used in flow
 */
export const variablesInFlow = (funnelDefinition: FunnelDefinitionRootNode): string[] => {
  const variables: string[] = []

  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'data') {
      node.actions.forEach((action: any) => {
        if (action.type === 'variable' && action.variableName) {
          variables.push(action.variableName)
        }
      })
    }
  })

  return [...new Set(variables)]
}

/**
 * Extract questions used in flow
 */
export const questionsInFlow = (funnelDefinition: FunnelDefinitionRootNode, questions: Question[] = []) => {
  let questionIds: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask && node.segmentGroupId) {
      questionIds.push(node.segmentGroupId)
    }
  })

  return questionIds
    .map((qId) => questions.find((q) => q.segmentGroupId === qId))
    .filter(Boolean)
    .map((q) => ({
      id: q!.id,
      label: stripHtml(renderText(q!.text)),
      ...q
    }))
}

/**
 * Extract input questions used in flow
 * Input questions are identified by segmentGroupId starting with 'raq_'
 */
export const inputQuestionsInFlow = (
  funnelDefinition: FunnelDefinitionRootNode,
  questions: Question[] = []
): { id: string; label: string }[] => {
  let questionIds: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'sg' && node.ask && node.segmentGroupId && node.segmentGroupId.startsWith('raq_')) {
      questionIds.push(node.segmentGroupId)
    }
  })

  return questionIds
    .map((qId) => {
      const question = questions.find((q) => q.id === qId)
      if (!question) {
        return null
      }
      return {
        id: question.id,
        label: stripHtml(renderText(question.text))
      }
    })
    .filter(Boolean) as { id: string; label: string }[]
}

/**
 * Extract offers used in flow
 */
export const offersInFlow = (funnelDefinition: FunnelDefinitionRootNode, offers: Offer[] = []) => {
  let offerIds: string[] = []
  funnelDefinitionEach(funnelDefinition, (node) => {
    if (node.type === 'offer' && node.offerId) {
      offerIds.push(node.offerId)
    }
  })

  const offersGottenById = offerIds.map((oId) => offers.find((o) => o.id === oId)).filter(Boolean)

  return offersGottenById.length
    ? offersGottenById.map((o) => ({
        id: o!.id,
        label: o!.name,
        ...o
      }))
    : []
}

/**
 * Get all visible things (questions + offers) in flow
 */
export const visibleThingsInFlow = (
  funnelDefinition: FunnelDefinitionRootNode,
  questions: Question[] = [],
  offers: Offer[] = []
) => [...questionsInFlow(funnelDefinition, questions), ...offersInFlow(funnelDefinition, offers)]

/**
 * Check if flow contains a specific question
 */
export const flowContainsQuestion = (flow: Flow, question: Question, questions: Question[] = []): boolean => {
  const questionsInThisFlow = questionsInFlow(flow.funnelDefinition, questions)
  return questionsInThisFlow.some((q) => q.id === question.id)
}

/**
 * The question a split node asks or routes by. Nodes reference it by question,
 * dimension or segment group ID.
 */
export const findNodeQuestion = <T extends Pick<Question, 'id' | 'dimensionId' | 'segmentGroupId'>>(
  questions: T[],
  segmentGroupId: string | null | undefined
): T | undefined =>
  segmentGroupId
    ? questions.find(
        (item) =>
          item.id === segmentGroupId || item.dimensionId === segmentGroupId || item.segmentGroupId === segmentGroupId
      )
    : undefined

/**
 * Get a node by its ID from a funnel definition
 * Searches through all nodes in the tree structure
 * Checks both 'id' and '_uuid' fields
 */
export const getNodeByNodeId = (
  funnelDefinition: FunnelDefinitionRootNode,
  nodeId: string
): FunnelDefinitionNode | null => {
  if (!nodeId || !funnelDefinition) {
    return null
  }

  let foundNode: FunnelDefinitionNode | null = null

  funnelDefinitionEach(funnelDefinition, (node) => {
    // Root is only a traversal container and cannot be selected by node ID.
    if (node.type !== 'root' && (node.id === nodeId || node._uuid === nodeId)) {
      foundNode = node
    }
  })

  return foundNode
}

/**
 * Recursively regenerate UUIDs for a node and all its children
 * Used when duplicating nodes to avoid ID conflicts
 */
const regenerateUuids = (node: any): void => {
  if (node._uuid) {
    node._uuid = uuid()
  }

  // Handle all standard child arrays: child, yes, no
  const childArrays = ['child', 'yes', 'no']
  childArrays.forEach((key) => {
    if (Array.isArray(node[key])) {
      node[key].forEach(regenerateUuids)
    }
  })

  // Handle split branches (for sg, random, highscore nodes)
  if (Array.isArray(node.split)) {
    node.split.forEach((branch: any) => {
      if (Array.isArray(branch.child)) {
        branch.child.forEach(regenerateUuids)
      }
    })
  }
}

/**
 * Check if a question for the given segmentGroupId is asked before the
 * target node in the flow. Checks both ancestors AND preceding siblings
 * in the same child array (since child arrays represent sequential execution).
 *
 * Used to determine if a router node's "Default" path is unreachable
 * because the question was already asked earlier in the same flow path.
 */
export const isQuestionAskedBeforeNode = (
  funnelDefinition: FunnelDefinitionRootNode,
  targetNodeUuid: string,
  segmentGroupId: string
): boolean => {
  const isAskingQuestion = (node: any): boolean =>
    node.type === 'sg' && node.ask && node.segmentGroupId === segmentGroupId

  // Walk a child array sequentially. For each node, check if it (or any
  // preceding sibling) asks the question. Nodes earlier in the array
  // execute before later ones.
  const findInRun = (nodeRun: any[], precedingAsks: boolean): boolean => {
    let askedBefore = precedingAsks
    for (const node of nodeRun) {
      const result = findInNode(node, askedBefore)
      if (result === true) {
        return true
      }
      // Track if this node asks the question (for subsequent siblings)
      if (isAskingQuestion(node)) {
        askedBefore = true
      }
    }
    return false
  }

  // Returns true if target found and question was asked before it,
  // false if target not found in this subtree
  const findInNode = (node: any, askedBefore: boolean): boolean => {
    // Check if this IS the target node
    if (node._uuid === targetNodeUuid) {
      return askedBefore
    }

    if (node.type === 'root') {
      return findInRun(node.child || [], askedBefore)
    }

    // For sg nodes: the node itself may ask the question, and its
    // split children execute AFTER the ask
    if (node.type === 'sg' || node.type === 'random' || node.type === 'highscore') {
      const askedHere = askedBefore || isAskingQuestion(node)
      if (node.split && Array.isArray(node.split)) {
        for (const sp of node.split) {
          if (findInRun(sp.child || [], askedHere)) {
            return true
          }
        }
      }
      return false
    }

    if (node.type === 'yesno') {
      return findInRun(node.yes || [], askedBefore) || findInRun(node.no || [], askedBefore)
    }

    return false
  }

  return findInNode(funnelDefinition, false)
}

/**
 * Deep clone a node and all its children, regenerating UUIDs
 * Used for Alt+Drag duplication to create a copy with fresh IDs
 */
export const deepCloneNode = (node: any): any => {
  // Deep clone using JSON serialization
  const clone = JSON.parse(JSON.stringify(node))

  // Regenerate all UUIDs to avoid conflicts
  regenerateUuids(clone)

  return clone
}
